import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gauge, Plus, Scale, Truck } from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '../../context/AuthContext';
import { documentsAPI } from '../../lib/api';
import { wetstockApi } from '../../lib/wetstockApi';
import { messageErreur } from '../../lib/erreurs';
import { libelle, nomLisible, optionsLibelles, ton } from '../../lib/libelles';
import { formatDate, formatDateHeure, formatHeure, formatNombre, formatPourcent } from '../../lib/formats';
import { chartVolume, to15c } from '../../lib/stationCalc';
import { PageHeader } from '../../components/ui/page-header';
import { Aide } from '../../components/ui/aide';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { BarrePagination } from '../../components/ui/pagination-bar';
import { usePagination, usePaginationServeur } from '../../hooks/usePagination';
import { useDerniereDemande } from '../../hooks/useDerniereDemande';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { LoadingState } from '../../components/ui/loading-state';
import { EmptyState } from '../../components/ui/empty-state';
import { StationPicker, useDroitsStation, useStations } from '../../components/stations/StationPicker';
import { TankGauge } from '../../components/stations/TankGauge';
import { RecalibrageDialog } from '../../components/stations/RecalibrageDialog';
import { Readout } from '../../components/stations/Readout';

/**
 * Cuves et réceptions (lot 031) : jauges des cuves avec autonomie, jaugeage
 * guidé (hauteur → litres du barème → litres à 15 °C), réceptions de camion,
 * suivi quotidien et cumulé de chaque cuve, ajustement motivé. Les chiffres
 * viennent du serveur (`station_wetstock.py`) ; l'aperçu du jaugeage passe par
 * `stationCalc.js`, la même règle que le serveur.
 */

const MOMENTS = ['DAY_OPEN', 'DAY_CLOSE', 'CONTROL'];

const PERIODES = [
  { value: '7', label: '7 derniers jours' },
  { value: '30', label: '30 derniers jours' },
];

const Champ = ({ id, label, aide, children }) => (
  <div className="grid gap-1.5">
    <label htmlFor={id} className="flex items-center gap-1 text-sm font-medium text-foreground">
      {label}
      {aide}
    </label>
    {children}
  </div>
);

const Panneau = ({ titre, aide, droite, children, className }) => (
  <section className={`flex flex-col rounded-xl border border-border bg-card shadow-sm ${className || ''}`}>
    <header className="flex flex-wrap items-center justify-between gap-2 px-4 pb-2 pt-3">
      <h2 className="flex items-center gap-1 text-base font-semibold">
        {titre}
        {aide}
      </h2>
      {droite}
    </header>
    <div className="flex min-h-0 flex-1 flex-col overflow-auto overscroll-y-contain px-1 pb-3">{children}</div>
  </section>
);

const CELLULES =
  'w-full border-collapse text-sm [&_td]:border-b [&_td]:border-border/60 [&_td]:px-3 [&_td]:py-2 [&_th]:whitespace-nowrap [&_th]:border-b [&_th]:border-border [&_th]:px-3 [&_th]:py-1.5 [&_th]:text-xs [&_th]:font-semibold [&_th]:text-muted-foreground [&_tr:last-child_td]:border-0 [&_tfoot_td]:border-t [&_tfoot_td]:border-border [&_tfoot_td]:font-semibold';

// Les trois tableaux du bas défilent dans leur panneau : les titres de colonnes restent visibles.
const CELLULES_DEFILANT = `${CELLULES} [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10 [&_thead_th]:bg-card`;

const vide = (valeur) => valeur === null || valeur === undefined || valeur === '';
const nombre = (valeur) => {
  if (vide(valeur)) return null;
  const n = Number(String(valeur).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

/** Litres entiers ; « — » sans valeur. */
const litres = (valeur) => (vide(valeur) ? '—' : formatNombre(valeur, 0));

/** Écart signé : « +3 », « −6 », « 0 ». */
const signe = (valeur, decimales = 0) => {
  if (vide(valeur)) return '—';
  const n = Number(valeur);
  const texte = formatNombre(Math.abs(n), decimales);
  if (n > 0) return `+${texte}`;
  if (n < 0) return `−${texte}`;
  return texte;
};

const pourcentSigne = (valeur) => (vide(valeur) ? '—' : `${Number(valeur) > 0 ? '+' : Number(valeur) < 0 ? '−' : ''}${formatPourcent(Math.abs(Number(valeur)), 2)}`);

/** « Mer. 22 » : le jour de la ligne de suivi. */
const jourCourt = (iso) => {
  if (!iso) return '—';
  const date = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return iso;
  const texte = date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', timeZone: 'UTC' });
  return texte.charAt(0).toUpperCase() + texte.slice(1);
};

/** AAAA-MM-JJ moins `jours` jours. */
const reculer = (iso, jours) => {
  const date = new Date(`${iso}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() - jours);
  return date.toISOString().slice(0, 10);
};

const optionCuve = (cuve) => ({
  value: cuve.id,
  label: [cuve.name, cuve.product_name].filter(Boolean).join(' · '),
  description: `${litres(cuve.current_volume_liters)} L sur ${litres(cuve.capacity_liters)} L`,
});

// --- Jaugeage guidé ----------------------------------------------------------------------------

const JaugeageDialog = ({ open, onOpenChange, stationId, cuves, cuveInitiale, momentInitial, onSaved }) => {
  const [cuveId, setCuveId] = useState('');
  const [moment, setMoment] = useState('DAY_CLOSE');
  const [hauteur, setHauteur] = useState('');
  const [volume, setVolume] = useState('');
  const [temperature, setTemperature] = useState('');
  const [eau, setEau] = useState('');
  const [photo, setPhoto] = useState(null);
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCuveId(cuveInitiale || cuves[0]?.id || '');
    setMoment(momentInitial || 'DAY_CLOSE');
    setHauteur('');
    setVolume('');
    setTemperature('');
    setEau('');
    setPhoto(null);
    setErreur('');
  }, [open, cuveInitiale, momentInitial, cuves]);

  const cuve = cuves.find((ligne) => ligne.id === cuveId) || null;
  const avecBareme = Boolean(cuve?.has_chart && (cuve?.chart_points || []).length);

  // Aperçu : même calcul que le serveur (barème puis correction à 15 °C).
  const apercu = useMemo(() => {
    if (!cuve) return { lu: null, corrige: null, probleme: '' };
    try {
      let lu = null;
      if (avecBareme) {
        if (nombre(hauteur) === null) return { lu: null, corrige: null, probleme: '' };
        lu = chartVolume(cuve.chart_points, nombre(hauteur));
      } else {
        lu = nombre(volume);
      }
      if (lu === null) return { lu: null, corrige: null, probleme: '' };
      const corrige = to15c(lu, nombre(temperature), cuve.expansion_coeff);
      return { lu, corrige, probleme: '' };
    } catch (probleme) {
      return { lu: null, corrige: null, probleme: probleme.message };
    }
  }, [cuve, avecBareme, hauteur, volume, temperature]);

  const enregistrer = async (event) => {
    event.preventDefault();
    setErreur('');
    setEnCours(true);
    try {
      let photoId;
      if (photo) {
        try {
          photoId = (await documentsAPI.upload(photo, 'OTHER'))?.id;
        } catch (probleme) {
          toast.warning(messageErreur(probleme, 'Photo non envoyée : le jaugeage est enregistré sans elle'));
        }
      }
      const cree = await wetstockApi.dips.record(stationId, {
        tank_id: cuveId,
        kind: moment,
        height_cm: avecBareme ? nombre(hauteur) : nombre(hauteur) ?? undefined,
        volume_liters: avecBareme ? undefined : nombre(volume),
        temperature_c: nombre(temperature) ?? undefined,
        water_height_cm: nombre(eau) ?? undefined,
        photo_document_id: photoId,
        source: photoId ? 'PHOTO' : 'MANUAL',
      });
      toast.success(`Jaugeage enregistré : ${litres(cree?.volume_15c)} L à 15 °C`);
      onSaved(cree);
      onOpenChange(false);
    } catch (probleme) {
      const message = messageErreur(probleme, 'Impossible d’enregistrer le jaugeage');
      setErreur(message);
      toast.error(message);
    } finally {
      setEnCours(false);
    }
  };

  const pret = cuveId && (avecBareme ? nombre(hauteur) !== null : nombre(volume) !== null) && !apercu.probleme;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1">
            Jauger une cuve
            <Aide titre="Jaugeage à 15 °C">
              Pigez la cuve, notez la hauteur et la température. Le barème de la cuve donne les litres lus, ramenés à
              15 °C avec le coefficient du produit. Sans barème, notez le volume lu. Le jaugeage de fermeture sert au
              suivi du jour et à l’arrêt de la journée.
            </Aide>
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={enregistrer} className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Champ id="jauge-cuve" label="Cuve">
              <SearchableSelect id="jauge-cuve" value={cuveId} onValueChange={setCuveId} options={cuves.map(optionCuve)} aria-label="Cuve" placeholder="Choisir la cuve" />
            </Champ>
          </div>
          <div className="sm:col-span-2">
            <Champ id="jauge-moment" label="Moment">
              <SearchableSelect id="jauge-moment" value={moment} onValueChange={setMoment} options={optionsLibelles('station_jaugeage', MOMENTS)} aria-label="Moment" />
            </Champ>
          </div>
          {avecBareme ? (
            <Champ id="jauge-hauteur" label="Hauteur pigée (cm)">
              <Input id="jauge-hauteur" type="number" inputMode="decimal" min="0" step="0.1" value={hauteur} onChange={(e) => setHauteur(e.target.value)} required className="text-right tabular-nums" />
            </Champ>
          ) : (
            <Champ id="jauge-volume" label="Volume lu (L)">
              <Input id="jauge-volume" type="number" inputMode="decimal" min="0" step="1" value={volume} onChange={(e) => setVolume(e.target.value)} required className="text-right tabular-nums" />
            </Champ>
          )}
          <Champ id="jauge-temperature" label="Température (°C)">
            <Input id="jauge-temperature" type="number" inputMode="decimal" min="-10" max="70" step="0.1" value={temperature} onChange={(e) => setTemperature(e.target.value)} className="text-right tabular-nums" />
          </Champ>
          {!avecBareme && (
            <Champ id="jauge-hauteur-libre" label="Hauteur pigée (cm)">
              <Input id="jauge-hauteur-libre" type="number" inputMode="decimal" min="0" step="0.1" value={hauteur} onChange={(e) => setHauteur(e.target.value)} className="text-right tabular-nums" />
            </Champ>
          )}
          <Champ id="jauge-eau" label="Hauteur d’eau (cm)">
            <Input id="jauge-eau" type="number" inputMode="decimal" min="0" step="0.1" value={eau} onChange={(e) => setEau(e.target.value)} className="text-right tabular-nums" />
          </Champ>
          <div className="sm:col-span-2">
            <Champ id="jauge-photo" label="Photo de la pige">
              <Input id="jauge-photo" type="file" accept="image/*" capture="environment" onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
            </Champ>
          </div>
          <div className="flex flex-wrap items-center gap-3 rounded-lg bg-muted/40 p-3 sm:col-span-2">
            <span className="text-sm text-muted-foreground">Volume lu</span>
            <Readout value={apercu.lu} decimals={0} unit="L" label="Volume lu" />
            <span className="text-sm text-muted-foreground">À 15 °C</span>
            <Readout value={apercu.corrige} decimals={0} unit="L" size="lg" label="Volume à 15 °C" />
          </div>
          {(apercu.probleme || erreur) && (
            <p className="text-sm font-medium text-destructive sm:col-span-2" role="alert">
              {apercu.probleme || erreur}
            </p>
          )}
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={enCours || !pret}>
              Enregistrer le jaugeage
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// --- Nouvelle réception -------------------------------------------------------------------------

const maintenantLocal = () => {
  const date = new Date();
  date.setSeconds(0, 0);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const ReceptionDialog = ({ open, onOpenChange, stationId, cuves, cuveInitiale, voitLesMontants, onSaved }) => {
  const vierge = useMemo(
    () => ({
      tank_id: cuveInitiale || cuves[0]?.id || '',
      delivered_liters_15c: '',
      loaded_liters_15c: '',
      delivery_note_number: '',
      truck_label: '',
      driver_name: '',
      supplier_name: '',
      expected_at: maintenantLocal(),
      unit_cost: '',
      freight_cost_per_liter: '',
    }),
    [cuveInitiale, cuves],
  );
  const [form, setForm] = useState(vierge);
  const [enCours, setEnCours] = useState(false);
  useEffect(() => {
    if (open) setForm(vierge);
  }, [open, vierge]);
  const changer = (cle) => (event) => setForm((avant) => ({ ...avant, [cle]: event?.target ? event.target.value : event }));
  const cuve = cuves.find((ligne) => ligne.id === form.tank_id);
  const creux = cuve ? Number(cuve.capacity_liters || 0) - Number(cuve.current_volume_liters || 0) : null;
  const deborde = creux !== null && nombre(form.delivered_liters_15c) !== null && nombre(form.delivered_liters_15c) > creux;

  const enregistrer = async (event) => {
    event.preventDefault();
    setEnCours(true);
    try {
      const date = new Date(form.expected_at);
      const cree = await wetstockApi.receptions.plan(stationId, {
        tank_id: form.tank_id,
        delivered_liters_15c: nombre(form.delivered_liters_15c),
        loaded_liters_15c: nombre(form.loaded_liters_15c) ?? undefined,
        delivery_note_number: form.delivery_note_number.trim() || undefined,
        truck_label: form.truck_label.trim() || undefined,
        driver_name: form.driver_name.trim() || undefined,
        supplier_name: form.supplier_name.trim() || undefined,
        expected_at: Number.isNaN(date.getTime()) ? undefined : date.toISOString(),
        unit_cost: voitLesMontants ? nombre(form.unit_cost) ?? undefined : undefined,
        freight_cost_per_liter: voitLesMontants ? nombre(form.freight_cost_per_liter) ?? undefined : undefined,
      });
      toast.success('Réception préparée');
      onSaved(cree);
      onOpenChange(false);
    } catch (probleme) {
      toast.error(messageErreur(probleme, 'Impossible de préparer la réception'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1">
            Préparer une réception
            <Aide titre="Litres à 15 °C">
              Recopiez les litres du bon de livraison, à 15 °C. Les litres chargés au dépôt servent à séparer le
              manquant de transport de l’écart de réception.
            </Aide>
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={enregistrer} className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Champ id="rec-cuve" label="Cuve">
              <SearchableSelect id="rec-cuve" value={form.tank_id} onValueChange={changer('tank_id')} options={cuves.map(optionCuve)} aria-label="Cuve" placeholder="Choisir la cuve" />
            </Champ>
          </div>
          <Champ id="rec-bon" label="Litres du bon (15 °C)">
            <Input id="rec-bon" type="number" inputMode="decimal" min="1" step="1" value={form.delivered_liters_15c} onChange={changer('delivered_liters_15c')} required aria-invalid={deborde} className="text-right tabular-nums" />
          </Champ>
          <Champ id="rec-charge" label="Chargés au dépôt (15 °C)">
            <Input id="rec-charge" type="number" inputMode="decimal" min="1" step="1" value={form.loaded_liters_15c} onChange={changer('loaded_liters_15c')} className="text-right tabular-nums" />
          </Champ>
          <Champ id="rec-numero" label="Numéro du bon">
            <Input id="rec-numero" value={form.delivery_note_number} onChange={changer('delivery_note_number')} maxLength={100} />
          </Champ>
          <Champ id="rec-camion" label="Camion">
            <Input id="rec-camion" value={form.truck_label} onChange={changer('truck_label')} maxLength={50} placeholder="AB-4127-MD" />
          </Champ>
          <Champ id="rec-chauffeur" label="Chauffeur">
            <Input id="rec-chauffeur" value={form.driver_name} onChange={changer('driver_name')} maxLength={255} />
          </Champ>
          <Champ id="rec-fournisseur" label="Fournisseur ou dépôt">
            <Input id="rec-fournisseur" value={form.supplier_name} onChange={changer('supplier_name')} maxLength={255} />
          </Champ>
          <Champ id="rec-arrivee" label="Arrivée prévue">
            <Input id="rec-arrivee" type="datetime-local" value={form.expected_at} onChange={changer('expected_at')} />
          </Champ>
          {voitLesMontants && (
            <>
              <Champ id="rec-cout" label="Coût du litre (FCFA)">
                <Input id="rec-cout" type="number" inputMode="decimal" min="0" step="0.01" value={form.unit_cost} onChange={changer('unit_cost')} className="text-right tabular-nums" />
              </Champ>
              <Champ id="rec-fret" label="Fret au litre (FCFA)">
                <Input id="rec-fret" type="number" inputMode="decimal" min="0" step="0.01" value={form.freight_cost_per_liter} onChange={changer('freight_cost_per_liter')} className="text-right tabular-nums" />
              </Champ>
            </>
          )}
          {deborde && (
            <div className="sm:col-span-2">
              <Badge variant="warning">Plus que la place libre en cuve ({litres(creux)} L)</Badge>
            </div>
          )}
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={enCours || !form.tank_id || nombre(form.delivered_liters_15c) === null}>
              Préparer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// --- Ajustement motivé -----------------------------------------------------------------------------

const AjustementDialog = ({ open, onOpenChange, cuve, onSaved }) => {
  const [quantite, setQuantite] = useState('');
  const [motif, setMotif] = useState('');
  const [enCours, setEnCours] = useState(false);
  useEffect(() => {
    if (open) {
      setQuantite('');
      setMotif('');
    }
  }, [open]);
  const valeur = nombre(quantite);
  const apres = cuve && valeur !== null ? Number(cuve.current_volume_liters || 0) + valeur : null;

  const enregistrer = async (event) => {
    event.preventDefault();
    setEnCours(true);
    try {
      await wetstockApi.adjust(cuve.id, { liters: valeur, reason: motif.trim() });
      toast.success('Stock de la cuve ajusté');
      onSaved();
      onOpenChange(false);
    } catch (probleme) {
      toast.error(messageErreur(probleme, 'Impossible d’ajuster le stock'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1">
            Ajuster le stock {cuve ? `· ${cuve.name}` : ''}
            <Aide titre="Ajustement motivé">
              Ajoutez (+) ou retirez (−) des litres avec un motif. L’ajustement est tracé dans le journal de stock et le
              journal d’audit ; il ne remplace pas une réception de camion.
            </Aide>
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={enregistrer} className="grid gap-4">
          <Champ id="aj-litres" label="Litres (+ ou −)">
            <Input id="aj-litres" type="number" inputMode="decimal" step="0.01" value={quantite} onChange={(e) => setQuantite(e.target.value)} required className="text-right tabular-nums" />
          </Champ>
          <Champ id="aj-motif" label="Motif">
            <Input id="aj-motif" value={motif} onChange={(e) => setMotif(e.target.value)} minLength={3} maxLength={500} required />
          </Champ>
          {apres !== null && (
            <p className="flex items-center justify-between rounded-lg bg-muted/40 px-3 py-2 text-sm">
              <span className="text-muted-foreground">Stock après ajustement</span>
              <span className={`font-semibold tabular-nums ${apres < 0 ? 'text-destructive' : ''}`}>{litres(apres)} L</span>
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={enCours || !valeur || motif.trim().length < 3 || (apres !== null && apres < 0)}>
              Ajuster
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// --- Page ----------------------------------------------------------------------------------------------

const TanksPage = () => {
  const navigate = useNavigate();
  const { canSeeMoney } = useAuth();
  const droits = useDroitsStation();
  // Un profil « achats » sans « cuves » voit les cuves et les réceptions, pas les jaugeages ni le suivi.
  const voitCuves = droits.peutLire('tanks');
  const { stations, loading: chargementStations } = useStations();
  const [stationId, setStationId] = useState('');
  const station = stations.find((ligne) => ligne.id === stationId) || null;

  const [vue, setVue] = useState(null);
  const [jaugeages, setJaugeages] = useState([]);
  const [totalJaugeages, setTotalJaugeages] = useState(0);
  const [receptions, setReceptions] = useState([]);
  const [totalReceptions, setTotalReceptions] = useState(0);
  // Réceptions et jaugeages se lisent page par page (le serveur donne le total): rien n'est coupé en silence.
  const paginationReceptions = usePaginationServeur(totalReceptions, { cle: 'receptions', reinitialiser: stationId });
  const paginationJaugeages = usePaginationServeur(totalJaugeages, { cle: 'jaugeages', reinitialiser: stationId });
  const { page: pageReceptions, taille: tailleReceptions } = paginationReceptions;
  const { page: pageJaugeages, taille: tailleJaugeages } = paginationJaugeages;
  const [chargement, setChargement] = useState(false);
  const [cuveSuivie, setCuveSuivie] = useState('');
  const [periode, setPeriode] = useState('7');
  const [suivi, setSuivi] = useState(null);
  const [chargementSuivi, setChargementSuivi] = useState(false);
  const [dialogue, setDialogue] = useState({ type: null, cuveId: null, moment: null });

  const charger = useCallback(async (id) => {
    if (!id) return;
    setChargement(true);
    const [laVue] = await Promise.allSettled([wetstockApi.overview(id)]);
    if (laVue.status === 'rejected') toast.error(messageErreur(laVue.reason, 'Impossible de charger les cuves'));
    setVue(laVue.status === 'fulfilled' ? laVue.value : null);
    setChargement(false);
  }, []);

  const demandeReceptions = useDerniereDemande();
  const demandeJaugeages = useDerniereDemande();
  const chargerReceptions = useCallback(async () => {
    if (!stationId) return;
    const perimee = demandeReceptions();
    try {
      const reponse = await wetstockApi.receptions.page(stationId, { page: pageReceptions, limit: tailleReceptions });
      if (perimee()) return;
      setReceptions(reponse.items || []);
      setTotalReceptions(reponse.total ?? (reponse.items || []).length);
    } catch (probleme) {
      if (perimee()) return;
      setReceptions([]);
      setTotalReceptions(0);
      toast.error(messageErreur(probleme, 'Impossible de charger les réceptions'));
    }
  }, [stationId, pageReceptions, tailleReceptions, demandeReceptions]);

  const chargerJaugeages = useCallback(async () => {
    const perimee = demandeJaugeages();
    if (!stationId || !voitCuves) {
      setJaugeages([]);
      setTotalJaugeages(0);
      return;
    }
    try {
      const reponse = await wetstockApi.dips.page(stationId, { page: pageJaugeages, limit: tailleJaugeages });
      if (perimee()) return;
      setJaugeages(reponse.items || []);
      setTotalJaugeages(reponse.total ?? (reponse.items || []).length);
    } catch (probleme) {
      if (perimee()) return;
      setJaugeages([]);
      setTotalJaugeages(0);
      toast.error(messageErreur(probleme, 'Impossible de charger les jaugeages'));
    }
  }, [stationId, voitCuves, pageJaugeages, tailleJaugeages, demandeJaugeages]);

  useEffect(() => {
    setVue(null);
    setSuivi(null);
    setCuveSuivie('');
    charger(stationId);
  }, [stationId, charger]);

  useEffect(() => {
    chargerReceptions();
  }, [chargerReceptions]);

  useEffect(() => {
    chargerJaugeages();
  }, [chargerJaugeages]);

  const cuves = useMemo(() => vue?.tanks || [], [vue]);

  useEffect(() => {
    if (!cuves.length) return;
    if (!cuveSuivie || !cuves.some((cuve) => cuve.id === cuveSuivie)) setCuveSuivie(cuves[0].id);
  }, [cuves, cuveSuivie]);

  const chargerSuivi = useCallback(async () => {
    if (!voitCuves || !stationId || !cuveSuivie || !vue?.today) return;
    setChargementSuivi(true);
    try {
      setSuivi(
        await wetstockApi.reconciliation(stationId, cuveSuivie, {
          dateFrom: reculer(vue.today, Number(periode) - 1),
          dateTo: vue.today,
        }),
      );
    } catch (probleme) {
      setSuivi(null);
      toast.error(messageErreur(probleme, 'Impossible de charger le suivi de la cuve'));
    } finally {
      setChargementSuivi(false);
    }
  }, [voitCuves, stationId, cuveSuivie, periode, vue?.today]);

  useEffect(() => {
    chargerSuivi();
  }, [chargerSuivi]);

  const recharger = () => {
    charger(stationId);
    chargerReceptions();
    chargerJaugeages();
    chargerSuivi();
  };

  const cuveDuSuivi = cuves.find((cuve) => cuve.id === cuveSuivie) || null;
  const sousLeSeuil = cuves.filter((cuve) => cuve.below_threshold).length;
  const autonomieBasse = cuves.filter((cuve) => !cuve.below_threshold && cuve.autonomy_days !== null && cuve.autonomy_days !== undefined && Number(cuve.autonomy_days) < 2).length;
  const aJauger = (vue?.closing_dips_missing || []).length;
  const enCours = (vue?.open_receptions || []).length;
  const peutJauger = droits.peutEcrire('tanks') && Boolean(station) && cuves.length > 0;
  const peutRecevoir = (droits.peutEcrire('purchases') || droits.peutEcrire('tanks')) && Boolean(station) && cuves.length > 0;
  const peutAjuster = droits.peutEcrire('stock_movements') && Boolean(cuveDuSuivi);
  const jours = suivi?.days || [];
  const [ongletBas, setOngletBas] = useState('suivi');
  const ongletActif = ongletBas === 'receptions' || !voitCuves ? 'receptions' : ongletBas;
  const paginationSuivi = usePagination(jours, { cle: 'suivi', reinitialiser: `${cuveSuivie}|${periode}` });

  return (
    <div className="mx-auto w-full max-w-page space-y-4 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Stations"
        title="Cuves et réceptions"
        description="Jaugeages à 15 °C, réceptions de camion, suivi des écarts et autonomie de chaque cuve."
        meta={station ? nomLisible(station.name, station.code) : undefined}
        alertes={[
          sousLeSeuil > 0 && {
            niveau: 'critique',
            texte: `${sousLeSeuil} cuve${sousLeSeuil > 1 ? 's' : ''} sous le seuil`,
            court: 'Sous le seuil',
            nombre: sousLeSeuil,
          },
          autonomieBasse > 0 && {
            niveau: 'attention',
            texte: `${autonomieBasse} cuve${autonomieBasse > 1 ? 's' : ''} à moins de 2 jours de vente`,
            court: 'Autonomie basse',
            nombre: autonomieBasse,
          },
          enCours > 0 && {
            niveau: 'info',
            texte: `${enCours} réception${enCours > 1 ? 's' : ''} attendue${enCours > 1 ? 's' : ''} ou en cours`,
            court: 'Réceptions',
            nombre: enCours,
          },
          aJauger > 0 && {
            niveau: 'info',
            texte: `${aJauger} cuve${aJauger > 1 ? 's' : ''} à jauger à la fermeture`,
            court: 'Fermeture à jauger',
            nombre: aJauger,
            action: peutJauger
              ? { label: 'Jauger', onClick: () => setDialogue({ type: 'jauger', cuveId: vue.closing_dips_missing[0], moment: 'DAY_CLOSE' }) }
              : undefined,
          },
        ].filter(Boolean)}
        actions={
          <>
            <StationPicker stations={stations} value={stationId} onChange={setStationId} />
            {peutJauger && (
              <Button type="button" onClick={() => setDialogue({ type: 'jauger', cuveId: cuveSuivie, moment: aJauger ? 'DAY_CLOSE' : 'CONTROL' })}>
                <Gauge className="h-4 w-4" aria-hidden="true" />
                Jauger
              </Button>
            )}
            {peutRecevoir && (
              <Button type="button" variant="outline" onClick={() => setDialogue({ type: 'reception', cuveId: cuveSuivie })}>
                <Truck className="h-4 w-4" aria-hidden="true" />
                Réception
              </Button>
            )}
          </>
        }
      />

      {!chargementStations && stations.length === 0 ? (
        <EmptyState
          variant="empty"
          title="Aucune station"
          description="Les cuves, jaugeages et réceptions apparaissent ici dès qu’une station a ses cuves."
          action={
            <Button type="button" onClick={() => navigate('/stations/reglages')}>
              Créer une station
            </Button>
          }
        />
      ) : !station ? (
        <EmptyState variant="empty" title="Choisissez une station" className="border-0" />
      ) : chargement && !vue ? (
        <LoadingState shape="table" />
      ) : cuves.length === 0 ? (
        <EmptyState
          variant="empty"
          title="Aucune cuve active"
          description="Ajoutez les cuves de la station et leur barème dans les réglages."
          action={
            <Button type="button" onClick={() => navigate('/stations/reglages?onglet=cuves')}>
              Ajouter une cuve
            </Button>
          }
        />
      ) : (
        <>
          <Panneau
            titre="Cuves"
            droite={vue?.today ? <span className="text-xs text-muted-foreground">Journée du {formatDate(vue.today)}</span> : null}
          >
            <div className="grid gap-4 px-3 pt-1 sm:grid-cols-2 xl:grid-cols-[repeat(auto-fit,minmax(17rem,1fr))]">
              {cuves.map((cuve) => (
                <button
                  key={cuve.id}
                  type="button"
                  onClick={() => setCuveSuivie(cuve.id)}
                  aria-pressed={cuve.id === cuveSuivie}
                  className={`relative flex flex-col items-center gap-4 rounded-xl border px-4 py-5 text-center transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                    cuve.id === cuveSuivie ? 'border-primary/60 bg-primary/5 shadow-sm' : 'border-border'
                  }`}
                >
                  {cuve.id === cuveSuivie && (
                    <span className="absolute right-3 top-3 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">Suivie</span>
                  )}
                  <TankGauge
                    variant="carte"
                    name={cuve.name}
                    productName={cuve.product_name}
                    volume={cuve.current_volume_liters}
                    capacity={cuve.capacity_liters}
                    threshold={cuve.min_threshold_liters}
                    autonomyDays={cuve.autonomy_days}
                    tone={cuve.product_code === 'SUPER' ? 'success' : 'primary'}
                  />
                  <div className="w-full border-t border-border/70 pt-3 text-xs text-muted-foreground">
                    {cuve.last_dip ? (
                      <>
                        <p className="m-0 font-medium text-foreground">
                          {`${libelle('station_jaugeage', cuve.last_dip.kind)} à ${formatHeure(cuve.last_dip.dipped_at)}`}
                        </p>
                        <p className="m-0 tabular-nums">{`${litres(cuve.last_dip.volume_15c)} L à 15 °C`}</p>
                      </>
                    ) : (
                      <p className="m-0">Pas de jaugeage récent</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </Panneau>

          <Tabs value={ongletActif} onValueChange={setOngletBas} className="space-y-4">
            <div className="sticky top-0 z-20 -mx-4 border-b border-border bg-background/90 px-4 py-2 backdrop-blur md:-mx-6 md:px-6 lg:top-16 lg:-mx-8 lg:px-8">
              <TabsList>
                {voitCuves && <TabsTrigger value="suivi">Suivi</TabsTrigger>}
                <TabsTrigger value="receptions">
                  Réceptions
                  {totalReceptions > 0 && <span className="ml-1.5 tabular-nums text-muted-foreground">{totalReceptions}</span>}
                </TabsTrigger>
                {voitCuves && (
                  <TabsTrigger value="jaugeages">
                    Jaugeages
                    {totalJaugeages > 0 && <span className="ml-1.5 tabular-nums text-muted-foreground">{totalJaugeages}</span>}
                  </TabsTrigger>
                )}
              </TabsList>
            </div>
            {voitCuves && (
              <TabsContent value="suivi" className="mt-0 flex flex-col gap-3 data-[state=inactive]:hidden lg:h-[calc(100vh-8.5rem)]">
          <Panneau
            className="min-h-0 flex-1"
            titre={`Suivi de ${cuveDuSuivi?.name || 'la cuve'}`}
            aide={
              <Aide titre="Suivi de la cuve">
                Stock attendu = stock d’ouverture + reçu − vendu aux index (ramené à 15 °C) ± ajustements. L’écart est le
                stock jaugé à la fermeture moins le stock attendu. L’écart cumulé divise la somme des écarts par la
                somme des litres vendus. Une ligne en rouge dépasse la tolérance (0,5 % des ventes ou 20 L).
              </Aide>
            }
            droite={
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground">Litres à 15 °C</span>
                <SearchableSelect
                  value={cuveSuivie}
                  onValueChange={setCuveSuivie}
                  options={cuves.map(optionCuve)}
                  aria-label="Cuve suivie"
                  className="h-8 w-48"
                />
                <SearchableSelect value={periode} onValueChange={setPeriode} options={PERIODES} aria-label="Période" className="h-8 w-44" />
                {peutAjuster && (
                  <Button type="button" variant="outline" size="sm" onClick={() => setDialogue({ type: 'ajuster', cuveId: cuveSuivie })}>
                    <Scale className="h-4 w-4" aria-hidden="true" />
                    Ajuster le stock
                  </Button>
                )}
                {droits.estAdministrateur && cuveDuSuivi && (
                  <Button type="button" variant="outline" size="sm" onClick={() => setDialogue({ type: 'recalibrer', cuveId: cuveSuivie })}>
                    <Gauge className="h-4 w-4" aria-hidden="true" />
                    Recalibrer le barème
                  </Button>
                )}
              </div>
            }
          >
            {chargementSuivi && !suivi ? (
              <LoadingState shape="table" />
            ) : jours.length === 0 ? (
              <p className="px-3 py-2 text-sm text-muted-foreground">Aucun jour à afficher</p>
            ) : (
              <table className={CELLULES_DEFILANT}>
                <thead>
                  <tr>
                    <th className="text-left">Jour</th>
                    <th className="text-right">Stock d’ouverture</th>
                    <th className="text-right">Reçu</th>
                    <th className="text-right">Vendu aux index</th>
                    <th className="text-right">Stock attendu</th>
                    <th className="text-right">Stock jaugé</th>
                    <th className="text-right">Écart</th>
                    <th className="text-right">Écart du jour</th>
                    <th className="text-right">Écart cumulé</th>
                  </tr>
                </thead>
                <tbody>
                  {paginationSuivi.lignes.map((jour) => (
                    <tr key={jour.date} className={jour.exceeds ? 'bg-destructive/5' : undefined}>
                      <td className="whitespace-nowrap font-medium">
                        {jourCourt(jour.date)}
                        {Number(jour.unassigned_sales_liters) !== 0 && !vide(jour.unassigned_sales_liters) && (
                          <Badge variant="warning" className="ml-2" title="Ventes entre la fermeture de la veille et l’ouverture du jour">
                            {litres(jour.unassigned_sales_liters)} L vendus hors jaugeage
                          </Badge>
                        )}
                      </td>
                      <td className="text-right tabular-nums">{litres(jour.opening_15c)}</td>
                      <td className="text-right tabular-nums">{litres(jour.received_15c)}</td>
                      <td className="text-right tabular-nums">{litres(jour.sold_15c)}</td>
                      <td className="text-right tabular-nums">{litres(jour.expected_15c)}</td>
                      <td className="text-right tabular-nums">{litres(jour.measured_15c)}</td>
                      <td className={`text-right tabular-nums ${jour.exceeds ? 'font-semibold text-destructive' : ''}`}>{signe(jour.variance_liters)}</td>
                      <td className={`text-right tabular-nums ${jour.exceeds ? 'font-semibold text-destructive' : ''}`}>{pourcentSigne(jour.variance_percent)}</td>
                      <td className="text-right tabular-nums">{pourcentSigne(jour.cumulative_percent)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>Période</td>
                    <td />
                    <td className="text-right tabular-nums">{litres(suivi?.totals?.received_15c)}</td>
                    <td className="text-right tabular-nums">{litres(suivi?.totals?.sold_15c)}</td>
                    <td />
                    <td />
                    <td className="text-right tabular-nums">{signe(suivi?.totals?.variance_liters)}</td>
                    <td />
                    <td className="text-right tabular-nums">{pourcentSigne(suivi?.cumulative_percent)}</td>
                  </tr>
                </tfoot>
              </table>
            )}
          </Panneau>
            {jours.length > 0 ? <BarrePagination pagination={paginationSuivi} nom="jours" className="mt-0 shrink-0" /> : null}
              </TabsContent>
            )}
            <TabsContent value="receptions" className="mt-0 flex flex-col gap-3 data-[state=inactive]:hidden lg:h-[calc(100vh-8.5rem)]">
            <Panneau
              className="min-h-0 flex-1"
              titre="Réceptions"
              droite={
                peutRecevoir && (
                  <Button type="button" variant="outline" size="sm" onClick={() => setDialogue({ type: 'reception', cuveId: cuveSuivie })}>
                    <Plus className="h-4 w-4" aria-hidden="true" />
                    Réception
                  </Button>
                )
              }
            >
              {receptions.length === 0 ? (
                <p className="px-3 py-2 text-sm text-muted-foreground">Aucune réception</p>
              ) : (
                <table className={CELLULES_DEFILANT}>
                  <thead>
                    <tr>
                      <th className="text-left">Camion</th>
                      <th className="text-left">Cuve</th>
                      <th className="text-right">Bon (15 °C)</th>
                      <th className="text-right">Reçu (15 °C)</th>
                      <th className="text-right">Écart</th>
                      <th className="text-left">État</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receptions.map((ligne) => (
                      <tr
                        key={ligne.id}
                        onClick={() => navigate(`/stations/receptions/${ligne.id}`)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') navigate(`/stations/receptions/${ligne.id}`);
                        }}
                        tabIndex={0}
                        className={`cursor-pointer hover:bg-muted/40 ${ligne.exceeds ? 'bg-destructive/5' : ''}`}
                      >
                        <td className="whitespace-nowrap font-medium">
                          {ligne.truck_label || 'Camion'}
                          <span className="block text-xs font-normal text-muted-foreground">
                            {ligne.delivery_note_number ? `Bon ${ligne.delivery_note_number}` : formatDateHeure(ligne.expected_at || ligne.created_at)}
                          </span>
                        </td>
                        <td>{[ligne.tank_name, ligne.product_name].filter(Boolean).join(' · ')}</td>
                        <td className="text-right tabular-nums">{litres(ligne.delivered_liters_15c)}</td>
                        <td className="text-right tabular-nums">{litres(ligne.received_liters_15c)}</td>
                        <td className={`text-right tabular-nums ${ligne.exceeds ? 'font-semibold text-destructive' : ''}`}>{signe(ligne.total_variance_liters)}</td>
                        <td>
                          <Badge variant={ton('station_reception', ligne.status)}>{libelle('station_reception', ligne.status)}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panneau>
            {totalReceptions > 0 ? <BarrePagination pagination={paginationReceptions} nom="réceptions" className="mt-0 shrink-0" /> : null}
            </TabsContent>
            {voitCuves && (
              <TabsContent value="jaugeages" className="mt-0 flex flex-col gap-3 data-[state=inactive]:hidden lg:h-[calc(100vh-8.5rem)]">
            <Panneau className="min-h-0 flex-1" titre="Derniers jaugeages">
              {jaugeages.length === 0 ? (
                <p className="px-3 py-2 text-sm text-muted-foreground">Aucun jaugeage</p>
              ) : (
                <table className={CELLULES_DEFILANT}>
                  <thead>
                    <tr>
                      <th className="text-left">Heure</th>
                      <th className="text-left">Cuve</th>
                      <th className="text-left">Moment</th>
                      <th className="text-right">Hauteur</th>
                      <th className="text-right">Temp.</th>
                      <th className="text-right">Volume lu</th>
                      <th className="text-right">À 15 °C</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jaugeages.map((ligne) => (
                      <tr key={ligne.id}>
                        <td className="whitespace-nowrap">{formatDateHeure(ligne.dipped_at)}</td>
                        <td>{ligne.tank_name || '—'}</td>
                        <td>{libelle('station_jaugeage', ligne.kind)}</td>
                        <td className="text-right tabular-nums">{vide(ligne.height_cm) ? '—' : `${formatNombre(ligne.height_cm, 1)} cm`}</td>
                        <td className="text-right tabular-nums">{vide(ligne.temperature_c) ? '—' : `${formatNombre(ligne.temperature_c, 1)} °C`}</td>
                        <td className="text-right tabular-nums">{litres(ligne.volume_liters)}</td>
                        <td className="text-right font-medium tabular-nums">{litres(ligne.volume_15c)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panneau>
            {totalJaugeages > 0 ? <BarrePagination pagination={paginationJaugeages} nom="jaugeages" className="mt-0 shrink-0" /> : null}
              </TabsContent>
            )}
          </Tabs>
        </>
      )}

      <JaugeageDialog
        open={dialogue.type === 'jauger'}
        onOpenChange={(ouvert) => !ouvert && setDialogue({ type: null })}
        stationId={stationId}
        cuves={cuves}
        cuveInitiale={dialogue.cuveId}
        momentInitial={dialogue.moment}
        onSaved={recharger}
      />
      <ReceptionDialog
        open={dialogue.type === 'reception'}
        onOpenChange={(ouvert) => !ouvert && setDialogue({ type: null })}
        stationId={stationId}
        cuves={cuves}
        cuveInitiale={dialogue.cuveId}
        voitLesMontants={canSeeMoney}
        onSaved={(cree) => cree?.id && navigate(`/stations/receptions/${cree.id}`)}
      />
      <RecalibrageDialog
        open={dialogue.type === 'recalibrer'}
        onOpenChange={(ouvert) => !ouvert && setDialogue({ type: null })}
        stationId={stationId}
        cuve={cuves.find((cuve) => cuve.id === dialogue.cuveId) || null}
        estAdministrateur={droits.estAdministrateur}
        onApplied={recharger}
      />
      <AjustementDialog
        open={dialogue.type === 'ajuster'}
        onOpenChange={(ouvert) => !ouvert && setDialogue({ type: null })}
        cuve={cuves.find((cuve) => cuve.id === dialogue.cuveId) || null}
        onSaved={recharger}
      />
    </div>
  );
};

export default TanksPage;
